import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import ListAndDetailsSubLayout from '@layouts/ListAndDetailsSubLayout';
import useNoodleApi from '@hooks/useNoodleApi';
import { format as formatDate } from 'date-fns';
import useIsMobile, { SIZES } from '@hooks/useIsMobile';
import getDataReferences from '@/tsClient/questionnaires/getDataReferences';
import Plus from '@/components/Icons/Plus';
import CreateDataReferenceModal from '@/modals/CreateDataReferenceModal';

const PAGE_SIZE = 20;

const ExternalData: React.FC = ({ children }) => {
  const pathParams = useParams<{ id?: string }>();
  const isOneColumn = useIsMobile(SIZES.md);
  const [searchTerms, setSearchTerms] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [dataReferences, setDataReferences] = useState<Awaited<ReturnType<typeof getDataReferences>>['items']>([]);
  const [numDataReferences, setNumDataReferences] = useState<number | undefined>(undefined);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { getData: getDataReferencesFn } = useNoodleApi(getDataReferences);

  const selectedId = pathParams?.id;

  const loadPage = useCallback(async ({
    page,
    search,
  }: {
    page: number;
    search?: string;
  }): Promise<void> => {
    setCurrentPage(page);
    setIsFetching(true);
    const getResponse = await getDataReferencesFn({
      page,
      perPage: PAGE_SIZE,
      search,
    });
    if (getResponse.data) {
      setDataReferences(getResponse.data.items);
      setNumPages(getResponse.data.numPages);
      setNumDataReferences(getResponse.data.numItems);
    }
    setIsFetching(false);
  }, [getDataReferencesFn]);

  const handlePageChange = (newPage: number): void => {
    loadPage({
      page: newPage,
      search: searchTerms ?? undefined,
    });
  };
  useEffect(() => {
    loadPage({
      page: 1,
      search: searchTerms ?? undefined,
    });
  }, [loadPage, searchTerms]);

  const items = useMemo<Parameters<typeof ListAndDetailsSubLayout>[0]['items']>(() => dataReferences.map(dr => ({
    bottomLine: formatDate(new Date(dr.createdAt), 'MMM do, hh:mm a'),
    href: getUrl(IDENTIFIERS.DASHBOARD_SET_EXTERNAL_DATA_DETAIL, { id:  dr.id }),
    id: dr.id,
    isSelected: dr.id === selectedId,
    label: dr.label || 'No name',
  })), [dataReferences, selectedId]);

  return (
    <ListAndDetailsSubLayout
      header={{
        actions: [
          {
            icon: Plus,
            label: 'Create external data set',
            onClick: () => {
              setIsCreateModalOpen(true);
            },
          },
        ],
        title: "External Data",
      }}
      items={items}
      itemsCount={numDataReferences && numDataReferences > 0 ? numDataReferences: undefined}
      selectedId={selectedId}
      ifNoItemsText={'No external data yet'}
      isFetchingList={isFetching}
      isOneColumn={isOneColumn}
      getDetailPagePath={({ id }) => getUrl(IDENTIFIERS.DASHBOARD_SET_EXTERNAL_DATA_DETAIL, { id })}
      onSearch={setSearchTerms}
      resourceType={'external-data'}
      pagination={{
        numPages,
        onChangePage: handlePageChange,
        page: currentPage,
      }}
    >
      {children}
      {isCreateModalOpen && (
        <CreateDataReferenceModal
          createVersion={false}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={() => {
            setIsCreateModalOpen(false);
            loadPage({
              page: currentPage,
            });
          }}
        />
      )}
    </ListAndDetailsSubLayout>
  );
};

export default ExternalData;